// Salary-schedule math shared by the Your Needs step (package options) and
// the pitch panel (charts). Data comes from GET districts/:id/pay-scale.

// 1 credit on a salary schedule = 10 clock hours (WA standard); the profile's
// "+N" columns and district_columns.hours are credit counts
export const HOURS_PER_CREDIT = 10;
export const FUTURE_YEARS = 5;
const MAX_TARGETS = 3;

import {HOURS_STEP} from '@/constants/packages';

// A schedule that no longer covers the current school year (July 1 rollover,
// same rule as the admin coverage buckets): still the best we have on file,
// but worth flagging to the student.
export function isStaleScale(scale) {
    if (!scale) return false;
    const now = new Date();
    const fallYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    return scale.end_year < fallYear + 1;
}

// Salary at a years-of-service value: the highest published step at or below
// it (schedules freeze at their top step, and some skip step numbers), floored
// to the first step for brand-new teachers.
export function salaryAt(lane, exp) {
    if (!lane.steps || lane.steps.length === 0) return null;
    let match = lane.steps[0];
    for (const step of lane.steps) {
        if (step.exp <= exp) {
            match = step;
        } else {
            break;
        }
    }
    return match.value;
}

// Every mapped lane on the schedule. `district_columns.hours` is normalized to
// the BA-track credit count (the Districts tool's contract), so distances
// between lanes are always measured on that one axis; lanes with hours === null
// have no clean mapping and are invisible to hours-based consumers by design.
function usableLanes(scale) {
    if (!scale || !scale.columns) return [];
    return scale.columns
        .filter(c => c.hours !== null && c.steps && c.steps.length)
        .sort((a, b) => a.position - b.position);
}

// Which education tracks a published lane belongs to, from its label. Combined
// labels ("BA + 90/MA +0", "BA+90 MA", "BA+135 or MA+45") belong to BOTH
// tracks; a label with no degree marker at all is treated as BA-track. Clock
// hours can never cross a degree boundary — a BA holder is never offered a
// pure-MA lane, no matter the credit math — so eligibility is gated per track
// while `hours` stays the shared axis for distances WITHIN a track.
function laneTracks(tier) {
    const t = String(tier || '');
    const ma = /\bM\.?A\b|MASTER/i.test(t);
    const doc = /PH\.?D|DOCTOR|\bDOC\b|DR\./i.test(t);
    const ba = /\bB\.?A\b/i.test(t) || (!ma && !doc);
    return {ba, ma, doc};
}

function laneEligible(tier, degree) {
    const {ba, ma, doc} = laneTracks(tier);
    if (degree === 'BA') return ba;
    if (degree === 'MA') return ma;
    if (degree === 'PHD') return ma || doc; // doctorates ride the MA lanes
    return true;
}

// Dropdown choices for "current column" built from the district's actual
// schedule — one option per mapped lane on the student's degree track, labeled
// with the district's own tier name, valued as the standard '+credits' string
// every consumer of meta.hours_column already parses. With no degree chosen
// yet, every mapped lane is offered. Returns null when no usable scale is on
// file (callers fall back to the standard ladder).
export function scaleColumnOptions(scale, degree) {
    const lanes = usableLanes(scale);
    if (!lanes.length) return null;

    const gated = lanes.filter(c => laneEligible(c.tier, degree));
    const seen = new Set();
    const options = [];
    for (const lane of (gated.length ? gated : lanes)) {
        const value = '+' + lane.hours;
        if (seen.has(value)) continue;
        seen.add(value);
        options.push({value, label: lane.tier});
    }
    return options;
}

// Where the user sits on the schedule and which lanes clock hours can reach.
// Returns null until degree, column, years and a usable schedule line up —
// targets never cross a degree boundary (hours alone can't earn an MA).
export function computePosition(scale, degree, column, years) {
    if (!scale || !column || column.charAt(0) !== '+' || !years) return null;
    const credits = parseFloat(column.slice(1));
    const exp = parseInt(years); // '17+' parses as 17
    if (isNaN(credits) || isNaN(exp)) return null;

    // Schedules assume at least a bachelor's; AA / "does not apply" can't chart
    if (degree !== 'BA' && degree !== 'MA' && degree !== 'PHD') return null;

    const lanes = usableLanes(scale).filter(c => laneEligible(c.tier, degree));
    if (lanes.length === 0) return null;

    let current = lanes[0];
    for (const lane of lanes) {
        if (lane.hours <= credits) current = lane;
    }

    const currentSalary = salaryAt(current, exp);
    if (currentSalary === null) return null;

    const targets = lanes.filter(l => l.hours > credits).slice(0, MAX_TARGETS).map(lane => {
        const nextYearSalary = salaryAt(lane, exp + 1);
        return {
            tier: lane.tier,
            lane,
            requiredHours: Math.ceil((lane.hours - credits) * HOURS_PER_CREDIT / HOURS_STEP) * HOURS_STEP,
            nextYearSalary,
            diff: nextYearSalary === null ? 0 : nextYearSalary - currentSalary
        };
    }).filter(t => t.nextYearSalary !== null && t.diff > 0);

    return {current, currentSalary, targets, exp, credits};
}
